import { Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { ProgressBarService } from '../services/progress-bar.service';
import { OAuthService } from '../services/oauth.service';
import { AnalyticsService } from '../services/analytics.service';


@Injectable()
export class ProgressInterceptor implements HttpInterceptor {
  constructor(
    private progressBarService: ProgressBarService,
    private authService: OAuthService,
    private injector: Injector
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.progressBarService.show(); // Show the progress bar

    const analytics = this.tryGetAnalytics();
    const startedAt = performance.now();
    analytics?.addHttpBreadcrumb({
      phase: 'start',
      method: request.method,
      url: request.urlWithParams
    });

    return next.handle(request).pipe(
      tap({
        next: (event) => {
          if (!(event instanceof HttpResponse)) return;
          analytics?.addHttpBreadcrumb({
            phase: 'success',
            method: request.method,
            url: request.urlWithParams,
            status: event.status,
            durationMs: performance.now() - startedAt
          });
        },
        error: (err: unknown) => {
          const httpErr = err as HttpErrorResponse;
          analytics?.addHttpBreadcrumb({
            phase: 'error',
            method: request.method,
            url: request.urlWithParams,
            status: typeof httpErr?.status === 'number' ? httpErr.status : undefined,
            durationMs: performance.now() - startedAt
          });
        }
      }),
      finalize(() => {
        this.progressBarService.hide(); // Hide the progress bar

      })
    );
  }

  /**
   * Note the `typeof` check: when the very first HTTP request of the page is fired from a
   * service constructor, AnalyticsService may still be mid-construction, and the injector
   * hands back Angular's CIRCULAR sentinel — an empty object — instead of throwing, because
   * the cyclic-dependency guard only exists in dev builds. Without the check the sentinel
   * would pass the null test and blow up on `addHttpBreadcrumb`.
   */
  private tryGetAnalytics(): AnalyticsService | null {
    try {
      const analytics = this.injector.get(AnalyticsService, null);
      return typeof analytics?.addHttpBreadcrumb === 'function' ? analytics : null;
    } catch {
      return null;
    }
  }
}
